import { defaultUrlTransform } from 'react-markdown'
import type { DocumentReference, ToolCallResult } from '../components/layout/chat-pane.types'

const DOC_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Preserve doc: links and relative .md paths that defaultUrlTransform would strip. */
export function chatMarkdownUrlTransform(
  url: string,
  ..._rest: unknown[]
): string {
  if (/^doc:/i.test(url)) return url
  const transformed = defaultUrlTransform(url)
  if (transformed) return transformed
  const normalized = url.replace(/^\.\//, '').replace(/\\/g, '/')
  if (!url.includes(':') && (normalized.endsWith('.md') || normalized.includes('/'))) {
    return url
  }
  return transformed
}

export function normalizeLinkTitle(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[—–]/g, '-')
}

function normalizeDocPath(path: string): string {
  return path.replace(/^\.\//, '').replace(/\\/g, '/').replace(/\/+$/, '')
}

function pathsMatch(a: string, b: string): boolean {
  const left = normalizeDocPath(a)
  const right = normalizeDocPath(b)
  return (
    left === right ||
    left.endsWith(`/${right}`) ||
    right.endsWith(`/${left}`) ||
    left.endsWith(right) ||
    right.endsWith(left)
  )
}

export function findDocumentByLinkText(
  linkText: string,
  documentsMap?: Map<string, DocumentReference>,
): { docId: string; doc: DocumentReference } | null {
  if (!documentsMap?.size) return null
  const normalized = normalizeLinkTitle(linkText)
  if (!normalized) return null

  let best: { docId: string; doc: DocumentReference; score: number } | null = null

  for (const [docId, doc] of documentsMap.entries()) {
    const titleNorm = normalizeLinkTitle(doc.title)
    if (!titleNorm) continue

    if (titleNorm === normalized) {
      return { docId, doc }
    }

    let score = 0
    if (normalized.includes(titleNorm) || titleNorm.includes(normalized)) {
      score = Math.min(normalized.length, titleNorm.length)
    } else {
      const linkWords = normalized.split(' ').filter((w) => w.length > 3)
      const titleWords = titleNorm.split(' ').filter((w) => w.length > 3)
      const overlap = linkWords.filter((w) => titleWords.includes(w)).length
      if (overlap >= 3) score = overlap
    }

    if (score > 0 && (!best || score > best.score)) {
      best = { docId, doc, score }
    }
  }

  return best ? { docId: best.docId, doc: best.doc } : null
}

export function extractDocumentLinksFromMarkdown(
  content: string,
): Map<string, DocumentReference> {
  const map = new Map<string, DocumentReference>()
  if (!content) return map

  const linkRe =
    /\[([^\]]+)\]\((doc:[0-9a-f-]{36}|[^)\s#?]+\.md[^)]*)\)/gi
  let match: RegExpExecArray | null
  while ((match = linkRe.exec(content)) !== null) {
    const title = match[1]?.trim()
    const target = match[2]?.trim()
    if (!target) continue

    if (/^doc:/i.test(target)) {
      const id = target.replace(/^doc:/i, '')
      if (!DOC_UUID_RE.test(id)) continue
      if (!map.has(id)) {
        map.set(id, {
          id,
          title: title || `Document ${id.slice(0, 8)}...`,
          type: 'note',
          fromMarkdown: true,
        })
      }
      continue
    }

    const path = normalizeDocPath(target)
    const syntheticId = `path:${path}`
    if (!map.has(syntheticId)) {
      map.set(syntheticId, {
        id: syntheticId,
        title: title || path.split('/').pop() || path,
        type: 'note',
        path,
      })
    }
  }

  return map
}

export function mergeDocumentsMaps(
  ...sources: Array<Map<string, DocumentReference> | undefined>
): Map<string, DocumentReference> {
  const merged = new Map<string, DocumentReference>()
  for (const source of sources) {
    if (!source) continue
    for (const [id, doc] of source.entries()) {
      merged.set(id, doc)
    }
  }
  return merged
}

/**
 * FlowNotes document UUID only. Basecamp search/query tools also return `results`
 * with numeric ids - those must never enter documentsMap (chip renderer calls
 * string methods on keys).
 */
function asFlowNotesDocumentId(raw: unknown): string | null {
  if (raw == null) return null
  const id = String(raw).trim()
  return DOC_UUID_RE.test(id) ? id : null
}

export function documentsMapFromToolResults(
  toolResults?: ToolCallResult[],
): Map<string, DocumentReference> {
  const map = new Map<string, DocumentReference>()
  if (!toolResults?.length) return map

  for (const tr of toolResults) {
    if (
      (tr.tool === 'createDocument' ||
        tr.tool === 'updateDocument' ||
        tr.tool === 'appendToDocument' ||
        tr.tool === 'createQuizDocument') &&
      tr.result
    ) {
      const id = asFlowNotesDocumentId(
        tr.result.documentId ?? tr.result.document?.id,
      )
      if (id) {
        map.set(id, {
          id,
          title:
            tr.result.title ??
            tr.result.document?.title ??
            `Document ${id.slice(0, 8)}...`,
          type: tr.result.documentType ?? tr.result.document?.type ?? 'note',
          path:
            tr.result.filePath ??
            tr.result.document?.filePath ??
            tr.result.path,
        })
      }
    }

    if (tr.tool === 'getDocument' && tr.result?.document) {
      const doc = tr.result.document
      const id = asFlowNotesDocumentId(doc.id ?? doc.documentId)
      if (!id) continue
      map.set(id, {
        id,
        title: doc.title ?? `Document ${id.slice(0, 8)}...`,
        type: doc.type ?? 'note',
        path: doc.path ?? doc.filePath,
      })
      continue
    }

    // Prefer documents[]; only treat results[] as docs for known FlowNotes tools.
    // searchBasecamp / queryBasecampTodos also use results[] with numeric Basecamp ids.
    const docs =
      Array.isArray(tr.result?.documents)
        ? tr.result.documents
        : tr.tool === 'searchDocuments' && Array.isArray(tr.result?.results)
          ? tr.result.results
          : null
    if (!docs) continue
    for (const doc of docs) {
      const id = asFlowNotesDocumentId(doc?.id ?? doc?.documentId)
      if (!id) continue
      map.set(id, {
        id,
        title: doc.title ?? `Document ${id.slice(0, 8)}...`,
        type: doc.type ?? 'note',
        path: doc.path ?? doc.filePath,
        excerpt: doc.excerpt ?? doc.summary,
      })
    }
  }

  return map
}

export function buildMessageDocumentsMap(message: {
  content?: string
  documentsMap?: Map<string, DocumentReference>
  toolResults?: ToolCallResult[]
}): Map<string, DocumentReference> {
  return mergeDocumentsMaps(
    message.documentsMap,
    documentsMapFromToolResults(message.toolResults),
    extractDocumentLinksFromMarkdown(message.content ?? ''),
  )
}

/** Absolute http(s) links are external - never treat as Maater storage paths. */
function isAbsoluteHttpUrl(href: string): boolean {
  return /^https?:\/\//i.test(href.trim())
}

/**
 * Prefix schemeless host/path URLs so remarkGfm treats them as links
 * (e.g. `3.basecamp.com/5744/...`).
 *
 * Must not match inside an existing hostname: `https://launchpad.37signals.com/...`
 * used to become `https://launchpad.https://37signals.com/...` because `.` was allowed
 * before the bare-host match.
 */
export function normalizeBareUrls(text: string): string {
  if (!text) return text
  return text.replace(/(?<![:/\w.])((?:[\w-]+\.)+[\w-]{2,}\/[^\s)>"]*)/g, (match) => {
    try {
      // eslint-disable-next-line no-new
      new URL(`https://${match}`)
      return `https://${match}`
    } catch {
      return match
    }
  })
}

/**
 * Domains assistant/chat markdown may link to externally (Launchpad OAuth, Basecamp, GitLab).
 * Subdomains match via endsWith (e.g. app.basecamp.com matches basecamp.com).
 */
export const ALLOWED_EXTERNAL_CHAT_DOMAINS = [
  '3.basecamp.com',
  'basecamp.com',
  'gitlab.com',
  'launchpad.37signals.com',
] as const

export function isAllowedExternalChatLink(href: string): boolean {
  let urlHost: string | null = null
  try {
    urlHost = new URL(href).hostname
  } catch {
    return false
  }
  return ALLOWED_EXTERNAL_CHAT_DOMAINS.some(
    (domain) => urlHost === domain || urlHost.endsWith(`.${domain}`),
  )
}

/**
 * True when this UUID came from a tool/create result, not only from assistant markdown.
 * Markdown-extracted ids are often near-miss hallucinations of the real createDocument id.
 */
export function isTrustedDocumentRef(
  id: string | undefined | null,
  documentsMap?: Map<string, DocumentReference>,
): boolean {
  if (!id) return false
  if (!documentsMap?.size) return true
  const doc = documentsMap.get(id)
  if (!doc) return false
  if (doc.fromMarkdown && !doc.path) return false
  return true
}

/**
 * Resolve a markdown link target to a document id or storage path openable by onDocumentClick.
 * Returns null for external http(s) URLs so the caller can open them outside the viewer.
 */
function knownDocId(
  id: string,
  documentsMap?: Map<string, DocumentReference>,
): boolean {
  return isTrustedDocumentRef(id, documentsMap)
}

export function resolveChatMarkdownDocumentLink(
  href: string | undefined | null,
  linkText: string,
  documentsMap?: Map<string, DocumentReference>,
  docIdFromRemark?: string | null,
): string | null {
  const remarkId = docIdFromRemark?.trim()
  if (remarkId && DOC_UUID_RE.test(remarkId) && knownDocId(remarkId, documentsMap)) {
    return remarkId
  }

  const rawHref = href?.trim()
  if (rawHref?.startsWith('doc:')) {
    const id = rawHref.slice(4)
    if (DOC_UUID_RE.test(id) && knownDocId(id, documentsMap)) return id
  }

  // Basecamp / GitLab / etc. contain "/" - without this guard they become
  // /api/documents/by-path?path=https%3A%2F%2Fapp.basecamp.com%2F... (.md/.html/README probes).
  if (rawHref && isAbsoluteHttpUrl(rawHref)) {
    return null
  }

  if (rawHref && rawHref !== '#' && rawHref !== '') {
    const normalizedHref = normalizeDocPath(rawHref)
    if (documentsMap?.size) {
      for (const [docId, doc] of documentsMap.entries()) {
        if (doc.path && pathsMatch(doc.path, normalizedHref)) {
          return docId.startsWith('path:') ? doc.path ?? docId : docId
        }
      }
    }
    if (normalizedHref.endsWith('.md') || normalizedHref.includes('/')) {
      return normalizedHref
    }
  }

  const byTitle = findDocumentByLinkText(linkText, documentsMap)
  if (byTitle) {
    return byTitle.docId.startsWith('path:')
      ? byTitle.doc.path ?? byTitle.docId.slice(5)
      : byTitle.docId
  }

  return null
}
